/**
 * Reglas de contrato de Homiii.
 *
 * OJO: esto es política de Homiii, NO de House and Flats. No vale para el resto
 * del catálogo (Spacest, FNS, anfitriones normales). Todo lo que se importe de
 * aquí tiene que estar condicionado a `isHomiiiAccommodation()`.
 *
 * Reglas, deducidas sondeando `POST /comprobar_fechas/`:
 *
 *   1. La entrada solo puede ser el día 1 del mes.
 *   2. La salida solo puede ser el último día del mes.
 *   3. Un contrato no puede CONTENER agosto, salvo que agosto sea su mes de inicio.
 *   4. La duración mínima es por habitación (unas aceptan 1 mes, otras exigen 4).
 *   5. No hay tope de antelación.
 *
 * Como todo se mide en meses completos, aquí un mes se representa como un índice
 * entero `year * 12 + (month - 1)`, que ordena y resta sin líos de zona horaria.
 */

export const HOMIII_USER_ID = process.env.VUE_APP_HOMIII_USER_ID || '6694';

/** Meses hacia adelante que se ofrecen en el selector. */
export const HORIZON_MONTHS = 36;

const AUGUST = 7; // 0 = enero

export function isHomiiiAccommodation(accommodation) {
    return !!accommodation &&
        String(accommodation.usuario_idusuario) === String(HOMIII_USER_ID);
}

/* ------------------------------------------------------------------ *
 * Índices de mes
 * ------------------------------------------------------------------ */

export function monthIndex(year, month0) {
    return (year * 12) + month0;
}

export function fromIndex(index) {
    return { year: Math.floor(index / 12), month0: index % 12 };
}

export function indexFromDate(date) {
    return monthIndex(date.getFullYear(), date.getMonth());
}

/**
 * 'YYYY-MM-DD' -> índice de mes, sin pasar por Date (evita el corrimiento de
 * un día que produce `new Date('2026-09-01')`, que se parsea como UTC).
 */
export function indexFromIso(iso) {
    if (!iso || typeof iso !== 'string') return null;
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return monthIndex(Number(match[1]), Number(match[2]) - 1);
}

export function isAugust(index) {
    return (index % 12) === AUGUST;
}

/** Día 1 del mes, como 'YYYY-MM-DD'. */
export function firstDayIso(index) {
    const { year, month0 } = fromIndex(index);
    return `${year}-${pad(month0 + 1)}-01`;
}

/** Último día del mes, como 'YYYY-MM-DD'. */
export function lastDayIso(index) {
    const { year, month0 } = fromIndex(index);
    // Día 0 del mes siguiente = último día de este mes; cubre bisiestos.
    const day = new Date(year, month0 + 1, 0).getDate();
    return `${year}-${pad(month0 + 1)}-${pad(day)}`;
}

/** Día 1 del mes como objeto Date local (el store espera Date o 'YYYY-MM-DD'). */
export function firstDayDate(index) {
    const { year, month0 } = fromIndex(index);
    return new Date(year, month0, 1);
}

export function lastDayDate(index) {
    const { year, month0 } = fromIndex(index);
    return new Date(year, month0 + 1, 0);
}

/** Meses de contrato entre dos índices, ambos incluidos. */
export function monthCount(startIndex, endIndex) {
    return (endIndex - startIndex) + 1;
}

/* ------------------------------------------------------------------ *
 * Reglas
 * ------------------------------------------------------------------ */

/**
 * Primer mes en el que se puede empezar un contrato.
 *
 * - Si hoy no es día 1, el contrato más temprano empieza el mes que viene.
 * - Si Homiii dijo "ya está reservada hasta el X", el primer mes libre es el
 *   siguiente al de X (los contratos siempre terminan a fin de mes).
 */
export function firstBookableIndex({ today = new Date(), occupiedUntil = null } = {}) {
    let earliest = indexFromDate(today);
    if (today.getDate() !== 1) {
        earliest += 1;
    }

    const occupied = indexFromIso(occupiedUntil);
    if (occupied !== null && occupied + 1 > earliest) {
        earliest = occupied + 1;
    }

    return earliest;
}

/**
 * Último mes en el que puede terminar un contrato que empieza en `startIndex`,
 * por la regla de agosto: el contrato no puede pasar por un agosto que no sea
 * su mes de inicio, así que tiene que acabar antes del siguiente agosto.
 */
export function lastEndBeforeAugust(startIndex) {
    let next = startIndex + 1;
    while (!isAugust(next)) {
        next += 1;
    }
    return next - 1;
}

/**
 * Rango de meses de salida válidos para una entrada dada. Es contiguo: desde el
 * que cumple la duración mínima hasta el anterior al siguiente agosto.
 *
 * Devuelve `null` si esa entrada no admite ningún contrato.
 */
export function validEndRange(startIndex, { minMonths = 1, horizonEnd = Infinity } = {}) {
    const from = startIndex + Math.max(1, minMonths) - 1;
    const to = Math.min(lastEndBeforeAugust(startIndex), horizonEnd);

    if (from > to) return null;

    return { from, to };
}

/**
 * ¿Se puede empezar un contrato en este mes? Solo si existe al menos una salida
 * válida: con un mínimo de 4 meses, empezar en junio es imposible porque agosto
 * caería dentro del contrato sin ser el mes de inicio.
 */
export function isValidStart(startIndex, { minMonths = 1, firstIndex = -Infinity, horizonEnd = Infinity } = {}) {
    if (startIndex < firstIndex) return false;
    if (startIndex > horizonEnd) return false;

    return validEndRange(startIndex, { minMonths, horizonEnd }) !== null;
}

export function isValidEnd(startIndex, endIndex, options = {}) {
    const range = validEndRange(startIndex, options);
    if (!range) return false;

    return endIndex >= range.from && endIndex <= range.to;
}

/**
 * Valida una selección completa y devuelve un motivo legible cuando falla.
 * El motivo viene como código + parámetros para que lo traduzca el componente.
 *
 * @returns {{ ok: boolean, code: string, params: object }}
 */
export function validateContract(startIndex, endIndex, { minMonths = 1, firstIndex = -Infinity, horizonEnd = Infinity } = {}) {
    if (startIndex === null || endIndex === null) {
        return { ok: false, code: 'incomplete', params: {} };
    }

    if (startIndex < firstIndex) {
        return { ok: false, code: 'occupied', params: { first: firstDayIso(firstIndex) } };
    }

    if (endIndex < startIndex) {
        return { ok: false, code: 'inverted', params: {} };
    }

    const months = monthCount(startIndex, endIndex);
    if (months < minMonths) {
        return { ok: false, code: 'too-short', params: { months: minMonths } };
    }

    // Agosto dentro del contrato sin ser el mes de inicio
    for (let i = startIndex + 1; i <= endIndex; i += 1) {
        if (isAugust(i)) {
            return { ok: false, code: 'august', params: { last: lastDayIso(lastEndBeforeAugust(startIndex)) } };
        }
    }

    if (endIndex > horizonEnd) {
        return { ok: false, code: 'out-of-horizon', params: {} };
    }

    return { ok: true, code: 'ok', params: {} };
}

function pad(n) {
    return String(n).padStart(2, '0');
}
